import type { ToolAnswer, ToolContext } from '../mcp-types';
import type { ActionRow } from './action-table';
import type { ShowTarget } from '../tools/show-highlight.service';

/**
 * The window's wait for a builder's click, from the moment the action starts, prefill included. Has to end before the
 * desktop's backstop (FORWARD_BACKSTOP_MS, six minutes) even with the grace added, or Claude would be told a timeout
 * while a click could still land.
 */
export const CONFIRM_LIMIT_MS = 5 * 60 * 1000;

/** A click already saving when the limit falls is given this long, so a save that lands isn't answered as not saved. */
export const CONFIRM_SAVING_GRACE_MS = 30 * 1000;

/** What a screen's commit reports when the builder presses its button. */
export type SaveOutcome =
  /** `pressed`: the button, when a screen has several that report under one commit. */
  | { outcome: 'saved'; saved: unknown; pressed?: string }
  | { outcome: 'failed'; reason: string }
  | { outcome: 'discarded' }
  /** `ran`: the action had already gone to the server when the screen went away, so it may have happened. */
  | { outcome: 'closed'; because?: string; ran?: boolean };

type WaitEnd =
  | { how: 'saved'; saved: unknown; pressed?: string }
  | { how: 'discarded'; lastFailure?: string }
  /** `stillSaving`: the builder's click was still on its way when the wait closed, so "not saved" would be a guess. */
  | { how: 'closed'; because: string; ran?: boolean; stillSaving?: boolean; lastFailure?: string }
  | { how: 'limit'; stillSaving: boolean; lastFailure?: string };

/** A wait that is holding the one slot: it may still be prefilling, or already showing the button. */
export interface OpenWait {
  /** Gives the slot back when the prefill ended without anything to press. */
  cancel(): void;
  /** Resolves when the wait is over, even if a prefill is still running; so a prefill that hangs can't hold the answer. */
  ended: Promise<WaitEnd>;
  /** Starts hearing the screen. Resolves when the builder has acted, or the limit or a closing has ended it. */
  untilEnd(): Promise<WaitEnd>;
}

interface Slot {
  commit: string;
  started: boolean;
  saving: boolean;
  lastFailure?: string;
  // Was (2026-10-30), when a closing during a prefill only left a note and the slot stayed taken: closedBefore?: string;
  timer: ReturnType<typeof setTimeout> | null;
  // Was (2026-10-30), when the promise only existed once the prefill was done: finish: ((end: WaitEnd) => void) | null;
  finish: (end: WaitEnd) => void;
}

export interface ConfirmLimits {
  limitMs?: number;
  graceMs?: number;
}

/**
 * The confirm lane's one wait. Tools open it; a screen reports its commits into it by a key both agree on, so a Save
 * elsewhere can't answer for a wait about something else. A screen reports whether or not anyone is waiting.
 */
export class ConfirmWaits {
  private slot: Slot | null = null;

  constructor(private readonly limits: ConfirmLimits = {}) {}

  get waiting(): boolean {
    return this.slot !== null;
  }

  /** Null while another wait holds the slot: two prefills at once could overwrite each other's form. */
  begin(commit: string): OpenWait | null {
    if (this.slot) return null;
    let finish: (end: WaitEnd) => void = () => undefined;
    const ended = new Promise<WaitEnd>((resolve) => { finish = resolve; });
    const slot: Slot = { commit, started: false, saving: false, timer: null, finish };
    this.slot = slot;
    // Was (2026-10-30): the limit started in untilEnd, after the prefill, so a slow prefill plus the grace could outlast the backstop.
    this.startTimer(slot, this.limits.limitMs ?? CONFIRM_LIMIT_MS, () => this.limitFell(slot));
    return {
      ended,
      cancel: () => {
        if (slot.timer !== null) clearTimeout(slot.timer);
        slot.timer = null;
        if (this.slot === slot) this.slot = null;
      },
      untilEnd: () => {
        slot.started = true;
        return ended;
      },
    };
  }

  /** The builder's click has started a save for this commit. */
  saving(commit: string): void {
    // A click during the prefill isn't the one this wait asks for, and its report isn't heard either.
    if (this.slot?.started && this.slot.commit === commit) this.slot.saving = true;
  }

  report(commit: string, outcome: SaveOutcome): void {
    const slot = this.slot;
    if (!slot || !slot.started || slot.commit !== commit) return;
    switch (outcome.outcome) {
      case 'saved': this.end(slot, { how: 'saved', saved: outcome.saved, ...(outcome.pressed ? { pressed: outcome.pressed } : {}) }); return;
      case 'failed':
        // The builder can fix it and press again, and Claude hears only how the wait ended.
        slot.saving = false;
        slot.lastFailure = outcome.reason;
        return;
      case 'discarded': this.end(slot, { how: 'discarded', ...lastFailureOf(slot) }); return;
      // Was (2026-10-30), before a closing carried the click's state: this.end(slot, { how: 'closed', because: outcome.because ?? 'the screen closed', ran: outcome.ran, ...lastFailureOf(slot) });
      case 'closed': this.closedEnd(slot, outcome.because ?? 'the screen closed', outcome.ran); return;
    }
  }

  /** The whole context went away: a project switch, a sign-out or the connection itself. */
  closeAll(because: string): void {
    const slot = this.slot;
    // Was (2026-10-30): if (slot.started) this.end(…); else slot.closedBefore = because; and a hung prefill never freed the slot.
    // Was (2026-10-30), before a closing carried the click's state: if (slot) this.end(slot, { how: 'closed', because, ...lastFailureOf(slot) });
    if (slot) this.closedEnd(slot, because);
  }

  /** The screen this commit lives on is gone. Unlike a report, this is heard during the prefill too: nothing else would end it. */
  close(commit: string, because: string): void {
    if (this.slot?.commit === commit) this.closeAll(because);
  }

  private closedEnd(slot: Slot, because: string, ran?: boolean): void {
    this.end(slot, { how: 'closed', because, ran, ...(slot.saving ? { stillSaving: true } : {}), ...lastFailureOf(slot) });
  }

  private limitFell(slot: Slot): void {
    if (slot.saving) {
      this.startTimer(slot, this.limits.graceMs ?? CONFIRM_SAVING_GRACE_MS, () => this.end(slot, { how: 'limit', stillSaving: true, ...lastFailureOf(slot) }));
      return;
    }
    this.end(slot, { how: 'limit', stillSaving: false, ...lastFailureOf(slot) });
  }

  private startTimer(slot: Slot, ms: number, fire: () => void): void {
    slot.timer = setTimeout(fire, ms);
  }

  private end(slot: Slot, end: WaitEnd): void {
    if (slot.timer !== null) clearTimeout(slot.timer);
    slot.timer = null;
    if (this.slot === slot) this.slot = null;
    slot.finish(end);
  }
}

function lastFailureOf(slot: Slot): { lastFailure?: string } {
  return slot.lastFailure === undefined ? {} : { lastFailure: slot.lastFailure };
}

/** What a prefill leaves: something to press, or an answer that needs no press. */
export type Prefilled =
  | { refused: string }
  | { answer: ToolAnswer }
  | {
      /** When the screen's button isn't the row's: Update on a finalized entity, for instance. */
      button?: string;
      /** Replaces the usual note, when the builder has something to do before pressing. */
      note?: string;
      /** Added to whatever the wait ends with, such as which of several values the prefill refused. */
      also?: Record<string, unknown>;
      /** Shapes what the screen reported into what the answer should carry. */
      describe?: (saved: unknown) => unknown;
      /** The answer to give instead when what was pressed didn't do what the row asked; null when it did. */
      verdict?: (saved: unknown, pressed: string) => ToolAnswer | null;
    };

export interface PressToCommitDeps {
  waits: Pick<ConfirmWaits, 'begin'>;
  highlight: { markToPress(target: ShowTarget, note: string): void; unmark(): void };
  /** The key the screen reports this commit's outcomes under. */
  commit: string;
  /** Fills the form. It must never fire a commit: only the builder's click does that. */
  prefill: (values: Record<string, unknown>, ctx: ToolContext, row: ActionRow) => Promise<Prefilled>;
}

export const PLEASE_PRESS_NOTE = 'Please press this. Claude can\'t press it for you.';

/**
 * A confirm-lane action on a screen with its own commit button: prefill, mark the button, wait for the outcome the
 * screen reports, answer with it. The screen tags the button `data-mcp-button="<its label>"` and calls
 * `saving` and `report` from the button's own handler; there is no call here that commits.
 */
export function pressToCommit(deps: PressToCommitDeps) {
  return async (values: Record<string, unknown>, ctx: ToolContext, row: ActionRow): Promise<ToolAnswer> => {
    const wait = deps.waits.begin(deps.commit);
    if (!wait) {
      return { outcome: 'refused', refusal: `Nothing was changed: the builder still has another action to press. Wait for that answer, then try ${row.label} again.` };
    }

    let prefilled: Prefilled;
    try {
      const first = await beforeTheWaitEnds(deps.prefill(values, ctx, row), wait);
      if ('ended' in first) return neverAsked(first.ended, row);
      prefilled = first.ready;
    } catch (err) {
      wait.cancel();
      throw err;
    }
    if ('refused' in prefilled) {
      wait.cancel();
      return { outcome: 'refused', refusal: prefilled.refused };
    }
    if ('answer' in prefilled) {
      wait.cancel();
      return prefilled.answer;
    }

    const button = prefilled.button ?? (row.commit.kind === 'press' ? row.commit.button : undefined);
    if (button === undefined) {
      wait.cancel();
      throw new Error(`${row.label} has no button for the builder to press.`);
    }
    deps.highlight.markToPress({ kind: 'button', id: button }, prefilled.note ?? PLEASE_PRESS_NOTE);
    const end = await wait.untilEnd();
    deps.highlight.unmark();
    const instead = end.how === 'saved' ? prefilled.verdict?.(end.saved, end.pressed ?? button) : null;
    if (instead) return instead;
    const described = end.how === 'saved' && prefilled.describe ? { ...end, saved: prefilled.describe(end.saved) } : end;
    return answerFor(described, button, prefilled.also ?? {});
  };
}

async function beforeTheWaitEnds<T>(work: Promise<T>, wait: OpenWait): Promise<{ ready: T } | { ended: WaitEnd }> {
  return Promise.race([work.then((ready) => ({ ready })), wait.ended.then((ended) => ({ ended }))]);
}

/** The wait ended before there was anything to press or confirm. A late prefill can still leave a form behind, so the answer can't promise a clean screen. */
function neverAsked(end: WaitEnd, row: ActionRow): ToolAnswer {
  const next = 'The builder was never asked, and nothing was saved by this call. A form may be part filled: read the screen before trying again.';
  return end.how === 'closed'
    ? { outcome: 'expired', result: { outcome: 'closed', because: end.because, message: `The wait ended while ${row.label} was being prepared: ${end.because}. ${next}` } }
    : { outcome: 'expired', result: { outcome: 'not-prepared', message: `${row.label} took too long to prepare. ${next}` } };
}

function answerFor(end: WaitEnd, button: string, also: Record<string, unknown>): ToolAnswer {
  switch (end.how) {
    case 'saved':
      return { outcome: 'done', changed: [`The builder pressed ${end.pressed ?? button} and it saved`], result: { ...also, outcome: 'saved', saved: end.saved } };
    case 'discarded':
      return {
        outcome: 'declined',
        by: 'builder',
        result: { ...also, outcome: 'discarded', ...failure(end.lastFailure), message: 'The builder left without saving. Not saved.' },
      };
    case 'closed':
      if (end.stillSaving) {
        return { outcome: 'expired', result: { ...also, outcome: 'still-saving', because: end.because, message: `The builder pressed ${button} but it was still saving when the wait ended: ${end.because}. It may have saved; check with read_project.` } };
      }
      return {
        outcome: 'expired',
        result: { ...also, outcome: 'closed', because: end.because, ...failure(end.lastFailure), message: `The wait ended: ${end.because}. Not saved by this wait; check with read_project before saying anything was.` },
      };
    case 'limit':
      if (end.stillSaving) {
        return { outcome: 'expired', result: { ...also, outcome: 'still-saving', message: `The builder pressed ${button} but it was still saving when the wait ran out. It may have saved; check with read_project.` } };
      }
      // The form and its button stay after the limit, so a later press would still commit: "not saved" would be a promise the screen doesn't keep.
      // Was (2026-10-30): `The builder pressed ${button} and the save failed, and the wait ran out without a retry that saved. Not saved.`
      if (end.lastFailure !== undefined) {
        return { outcome: 'expired', result: { ...also, outcome: 'failed', reason: end.lastFailure, message: `The builder pressed ${button} and the save failed, and it isn't saved so far. The ${button} button is still on the screen and the builder could still press it. Read the screen before trying again.` } };
      }
      // Was (2026-10-30): `The builder didn't press ${button} in time. Not saved; the edits are still on the screen for the builder to keep or discard.`
      return { outcome: 'expired', result: { ...also, outcome: 'not-pressed', message: `The builder hasn't pressed ${button} so far, so it isn't saved so far. The ${button} button is still on the screen and the builder could still press it. Read the screen before trying again.` } };
  }
}

function failure(reason: string | undefined): { lastFailure?: string } {
  return reason === undefined ? {} : { lastFailure: reason };
}

/** Long enough that a click aimed at what was on screen before can't land on a card that has just appeared. */
export const OVERLAY_ARM_MS = 1000;

/** The card the builder sees, for the layer that draws it. */
export interface OverlayCard {
  title: string;
  says: string;
  confirmLabel: string;
  armed: boolean;
  running: boolean;
  /** Why the last click failed, shown on the card; the builder can try again or decline. */
  failure?: string;
}

interface OverlayAsk {
  title: string;
  says: string;
  confirmLabel: string;
  /** Runs the action; resolves to why it failed, or null. */
  confirm(): Promise<string | null>;
  decline(): void;
}

/** The one card on screen, and the builder's two buttons on it. Holds no Angular, so a check can drive it without a page. */
export class OverlayConfirmations {
  current: OverlayCard | null = null;
  private ask: OverlayAsk | null = null;
  private armTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<() => void>();

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  /** Puts the card up; the function it returns takes this card down. */
  show(ask: OverlayAsk): () => void {
    const card: OverlayCard = { title: ask.title, says: ask.says, confirmLabel: ask.confirmLabel, armed: false, running: false };
    this.current = card;
    this.ask = ask;
    this.armTimer = setTimeout(() => {
      card.armed = true;
      this.changed();
    }, OVERLAY_ARM_MS);
    this.changed();
    return () => {
      if (this.current !== card) return;
      if (this.armTimer !== null) clearTimeout(this.armTimer);
      this.armTimer = null;
      this.current = null;
      this.ask = null;
      this.changed();
    };
  }

  async confirm(): Promise<void> {
    const card = this.current;
    const ask = this.ask;
    if (!card || !ask || !card.armed || card.running) return;
    card.running = true;
    card.failure = undefined;
    this.changed();
    const failure = await ask.confirm();
    card.running = false;
    card.failure = failure ?? undefined;
    if (this.current === card) this.changed();
  }

  decline(): void {
    const card = this.current;
    if (!card || !card.armed || card.running) return;
    this.ask?.decline();
  }

  private changed(): void {
    for (const listener of this.listeners) listener();
  }
}

/** What an action with no form prepares: why it can't run, an answer that needs no click, or the card to show. */
export type Prepared =
  | { refused: string }
  | { answer: ToolAnswer }
  | {
      /** The card's heading; the row's label unless a plainer one fits. */
      title?: string;
      /** What the action does, in the builder's words. Shown on the card. */
      says: string;
      confirmLabel?: string;
      also?: Record<string, unknown>;
      /** Runs what the screen's own button runs. Only the builder's click reaches it. */
      run: () => Promise<SaveOutcome>;
    };

export interface OverlayToCommitDeps {
  waits: Pick<ConfirmWaits, 'begin' | 'saving' | 'report'>;
  overlay: Pick<OverlayConfirmations, 'show'>;
  /** The key its outcomes go under; the row's id unless a screen reports the same commit from elsewhere. */
  commit?: string;
  /** Checks the action can run and words it. It must never run the action: only the card's button does. */
  prepare: (values: Record<string, unknown>, ctx: ToolContext, row: ActionRow) => Promise<Prepared>;
}

/**
 * A confirm-lane action that is one click with no form: a card in Zavo says what it does, and the action runs only on
 * the builder's click. Shares the press's slot, limit and outcomes, so Claude can't tell them apart except by the words.
 */
export function overlayToCommit(deps: OverlayToCommitDeps) {
  return async (values: Record<string, unknown>, ctx: ToolContext, row: ActionRow): Promise<ToolAnswer> => {
    const commit = deps.commit ?? row.id;
    const wait = deps.waits.begin(commit);
    if (!wait) {
      return { outcome: 'refused', refusal: `Nothing was changed: the builder still has another action to press. Wait for that answer, then try ${row.label} again.` };
    }

    let prepared: Prepared;
    try {
      const first = await beforeTheWaitEnds(deps.prepare(values, ctx, row), wait);
      if ('ended' in first) return neverAsked(first.ended, row);
      prepared = first.ready;
    } catch (err) {
      wait.cancel();
      throw err;
    }
    if ('refused' in prepared) {
      wait.cancel();
      return { outcome: 'refused', refusal: prepared.refused };
    }
    if ('answer' in prepared) {
      wait.cancel();
      return prepared.answer;
    }

    const { run } = prepared;
    let over = false;
    const takeDown = deps.overlay.show({
      title: prepared.title ?? row.label,
      says: prepared.says,
      confirmLabel: prepared.confirmLabel ?? 'Confirm',
      confirm: async () => {
        deps.waits.saving(commit);
        const outcome = await run().catch((err: unknown): SaveOutcome => ({ outcome: 'failed', reason: err instanceof Error ? err.message : String(err) }));
        // A run outlasting its wait must not answer for the next one on the same row.
        if (over) return null;
        deps.waits.report(commit, outcome);
        return outcome.outcome === 'failed' ? outcome.reason : null;
      },
      decline: () => deps.waits.report(commit, { outcome: 'discarded' }),
    });
    const end = await wait.untilEnd();
    over = true;
    takeDown();
    return overlayAnswerFor(end, prepared.also ?? {});
  };
}

function overlayAnswerFor(end: WaitEnd, also: Record<string, unknown>): ToolAnswer {
  switch (end.how) {
    case 'saved':
      return { outcome: 'done', changed: ['The builder confirmed it and it was done'], result: { ...also, outcome: 'saved', saved: end.saved } };
    case 'discarded':
      return { outcome: 'declined', by: 'builder', result: { ...also, outcome: 'discarded', ...failure(end.lastFailure), message: 'The builder declined. Nothing was changed.' } };
    case 'closed':
      if (end.ran) {
        return { outcome: 'expired', result: { ...also, outcome: 'still-saving', because: end.because, message: `The builder confirmed it and it ran, but ${end.because}. It may have happened; check with read_screen before saying either way.` } };
      }
      if (end.stillSaving) {
        return { outcome: 'expired', result: { ...also, outcome: 'still-saving', because: end.because, message: `The builder confirmed it but it was still running when the wait ended: ${end.because}. It may have happened; check with read_screen.` } };
      }
      return { outcome: 'expired', result: { ...also, outcome: 'closed', because: end.because, ...failure(end.lastFailure), message: `The wait ended: ${end.because}. Not done by this wait; check with read_screen before saying anything was.` } };
    case 'limit':
      if (end.stillSaving) {
        return { outcome: 'expired', result: { ...also, outcome: 'still-saving', message: 'The builder confirmed it but it was still running when the wait ran out. It may have happened; check with read_screen.' } };
      }
      if (end.lastFailure !== undefined) {
        return { outcome: 'expired', result: { ...also, outcome: 'failed', reason: end.lastFailure, message: 'The builder confirmed it and it failed, and the wait ran out without a retry that worked. Nothing was changed.' } };
      }
      return { outcome: 'expired', result: { ...also, outcome: 'not-pressed', message: 'The builder didn\'t confirm it in time. Nothing was changed.' } };
  }
}
